/**
 * Environment Configuration Service
 * Centralized configuration for environment variables
 */

export interface EnvironmentConfig {
  api: {
    routeRoyal: string;
    timeout: number;
    webhookSecret: string;
    clientId?: string;
    clientSecret?: string;
  };
  supabase: {
    url: string;
    anonKey: string;
    serviceRoleKey: string;
  };
  turnstile: {
    siteKey: string;
    secretKey: string;
  };
  domains: {
    royale: string[];
    routeRoyal: string[];
  };
  development: {
    port: number;
  };
  features: {
    debugMode: boolean;
    enableCache: boolean;
    cspEnabled: boolean;
  };
  analytics: {
    gaTrackingId?: string | undefined;
  };
}

/**
 * Get environment configuration
 * Loads configuration from environment variables with sensible defaults
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Support both NEXT_PUBLIC_API_URL and ROUTE_ROYAL_API_URL
  // NEXT_PUBLIC_API_URL takes precedence (aligned with Sites API guide)
  const apiUrl = 
    process.env.NEXT_PUBLIC_API_URL || 
    process.env.ROUTE_ROYAL_API_URL || 
    'https://api.routeroyal.com';
  
  // Remove trailing slashes for consistency
  const normalizedApiUrl = apiUrl.replace(/\/+$/, '');
  
  return {
    api: {
      routeRoyal: normalizedApiUrl,
      timeout: parseInt(process.env.API_TIMEOUT || '30000', 10),
      webhookSecret: process.env.ROUTE_ROYAL_WEBHOOK_SECRET || '',
      ...(process.env.ROUTE_ROYAL_CLIENT_ID && { clientId: process.env.ROUTE_ROYAL_CLIENT_ID }),
      ...(process.env.ROUTE_ROYAL_CLIENT_SECRET && { clientSecret: process.env.ROUTE_ROYAL_CLIENT_SECRET }),
    },
    supabase: {
      url: process.env.NEXT_PUBLIC_SUPABASE_URL || '',
      anonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '',
      serviceRoleKey: process.env.SUPABASE_SERVICE_ROLE_KEY || '',
    },
    turnstile: {
      siteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '',
      secretKey: process.env.TURNSTILE_SECRET_KEY || '',
    },
    domains: {
      // Royale domains removed - no longer used
      // Allys will be at allys.routeroyal.com (tenant domain)
      royale: (process.env.ROYALE_DOMAINS || '').split(',').filter(d => d.trim()).map(d => d.trim()),
      routeRoyal: (process.env.ROUTE_ROYAL_DOMAINS || 'routeroyal.com,www.routeroyal.com').split(',').map(d => d.trim()),
    },
    development: {
      port: parseInt(process.env.PORT || process.env.DEV_PORT || '4002', 10),
    },
    features: {
      debugMode: process.env.NODE_ENV === 'development' || process.env.DEBUG_MODE === 'true',
      enableCache: process.env.ENABLE_CACHE !== 'false',
      cspEnabled: process.env.NEXT_PUBLIC_CSP_ENABLED !== 'false',
    },
    analytics: {
      gaTrackingId: process.env.NEXT_PUBLIC_GA_TRACKING_ID || undefined,
    },
  };
}

/**
 * Get domain configuration for a specific environment
 */
export function getDomainConfig() {
  const config = getEnvironmentConfig();
  
  // Add localhost development domains
  const devDomains = [
    `localhost:${config.development.port}`,
    'localhost', // Also match localhost without port
  ];
  
  return {
    // Only include dev domains if royale domains are configured
    royale: config.domains.royale.length > 0 ? [...config.domains.royale, ...devDomains] : devDomains,
    // In development, treat localhost as Route Royal domain (show marketing page)
    routeRoyal: process.env.NODE_ENV === 'development' 
      ? [...config.domains.routeRoyal, ...devDomains]
      : config.domains.routeRoyal,
  };
}

/**
 * Singleton instance
 * NOTE: In serverless environments (like Vercel), this instance might be reused across requests.
 * However, since environment variables are static during execution, caching this is safe and efficient.
 * If runtime configuration updates are needed, this caching strategy should be revisited.
 */
let configInstance: EnvironmentConfig | null = null;

export function getConfig(forceRefresh = false): EnvironmentConfig {
  if (!configInstance || forceRefresh) {
    configInstance = getEnvironmentConfig();
  }
  return configInstance;
}

